import { Divider, Tab, Tabs, Theme, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  Identifier,
  List,
  ListContextProvider,
  ListProps,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import { CustomDatetime } from "../../../Components/CustomDatetime";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultIsActiveFilter, DefaultPerPageSize } from "../../../Constants/Constant";
import { ActivePassiveStatus } from "../../../Enums/Active";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceCustomer } from "../Customer/Constant/Source";
import { SourceMelCategory } from "../MelCategory/Constant/Source";
import { MelCategoryCustomerExcelHeader, MelCategoryCustomerExcelName } from "./Constant/Excel";
import { SourceMelCategoryCustomer, TitleMelCategoryCustomer } from "./Constant/Source";

const MelCategoryCustomerFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <ReferenceInput
      label="Customer"
      source="customerId"
      reference={SourceCustomer}
      perPage={99999}
      translateChoice={false}
      alwaysOn
    >
      <SelectInput optionText="customerName" optionValue="id" fullWidth />
    </ReferenceInput>
    <ReferenceInput
      label="Category Name"
      source="categoryId"
      perPage={99999}
      reference={SourceMelCategory}
      translateChoice={false}
      alwaysOn
    >
      <SelectInput optionText="categoryName" optionValue="id" fullWidth />
    </ReferenceInput>
    <TextInput label="Category Code" source="categoryCode__contains" alwaysOn />
    <TextInput label="Category Name" source="categoryName__contains" alwaysOn />
    <CustomDatetime source="createdDate__gte" label="Tarih(>=)" />
    <CustomDatetime source="createdDate__lte" label="Tarih (<=)" />
  </Filter>
);

const ExportToExcel = data => {
  ExcelExport(
    MelCategoryCustomerExcelHeader,
    data.map(v => [
      // v.categoryCode,
      // v.categoryName,
      v.createdBy,
      v.createdDate,
    ]),
    MelCategoryCustomerExcelName
  );
};

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceMelCategoryCustomer,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: true }
  );
  const { total: totalIsPassive } = useGetList(
    SourceMelCategoryCustomer,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: false }
  );

  return {
    active: totalIsActive,
    passive: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const [isActive, setIsActive] = useState<Identifier[]>([] as Identifier[]);
  const [isPassive, setIsPassive] = useState<Identifier[]>([] as Identifier[]);
  const classes = useDatagridStyles();
  const totals = useGetTotals(filterValues) as any;

  useEffect(() => {
    if (ids && ids !== filterValues.isActive__eq) {
      switch (filterValues.isActive__eq) {
        case true:
          setIsActive(ids);
          break;
        case false:
          setIsPassive(ids);
          break;
      }
    }
  }, [ids, filterValues.isActive__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, isActive__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  const selectedIds = filterValues.isActive__eq == true ? isActive : filterValues.isActive__eq == false;

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.isActive__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {ActivePassiveStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.name} (${totals[choice.name]})` : choice.name}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />
      {isXSmall ? (
        <ListContextProvider value={{ ...listContext, ids: selectedIds }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          value={{
            ...listContext,
            ids: filterValues.isActive_eq ? isActive : isPassive,
          }}
        >
          <Datagrid {...props} optimized rowClick="edit">
            <ReferenceField label="CUSTOMER INFO" source="customerId" reference={SourceCustomer} link="">
              <TextField source="customerName" />
            </ReferenceField>
            <ReferenceField label="CATEGORY " source="categoryId" reference={SourceMelCategory} link="">
              <TextField source="categoryName" />
            </ReferenceField>
            <TextField source="rddTimeInside" label="RDD TIME INSIDE" />
            <TextField source="rddTimeOutside" label="RDD TIME OUTSIDE" />
            <TextField source="successRate" label="SUCCESS RATE" />
            <CreatedByField label="CREATED BY" link={false} />
            <CustomDateField source="createdDate" label="CREATED DATE" showTime />
          </Datagrid>
        </ListContextProvider>
      )}
    </Fragment>
  );
};

const MelCategoryCustomerList: FC<ListProps> = props => (
  <List
    {...props}
    title={TitleMelCategoryCustomer.list}
    filterDefaultValues={DefaultIsActiveFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
    filters={<MelCategoryCustomerFilter />}
  >
    <TabbedDatagrid />
  </List>
);

export default MelCategoryCustomerList;
